/**
 * BimTemplateEditor.jsx — the BIM tab of TemplatesWorkspace (Supabase-backed via
 * services/bim.js; schema database/111_bim.sql).
 *
 *   BimTemplatesList       — the tab's list: System Templates as group headers with
 *                            their Element Templates beneath; search; + New Element.
 *   BimTemplateEditorView  — per-element-template editor: template fields + four
 *                            per-phase criteria sections (drag-reorder via the shared
 *                            DragList, inline text edit, model/coord kind toggle,
 *                            add/delete).
 *
 * NO "Push to Live" here, by design: live BIM Elements read criteria directly from
 * these rows (uuid-keyed state on the element survives edits), so template edits
 * propagate instantly — unlike the milestone editor's snapshot+push model.
 */
import { useState, useEffect, useRef } from 'react'
import { confirmDialog } from './Dialog.jsx'
import { DragList, S } from './TemplatesWorkspace.jsx'
import {
  BIM_PHASES, BIM_PHASE_LABELS,
  getBimTemplates, saveBimSystemTemplate, saveBimElementTemplate, deleteBimElementTemplate,
  saveBimCriterion, deleteBimCriterion, reorderBimCriteria,
  uploadBimVisualAid, removeBimVisualAid,
  backfillBimTemplateImages, backfillBimVisualAids,
} from '../services/bim.js'

const KIND_COLORS = { model: 'var(--aj-text-mid)', coord: '#b8540a' }

/* ── One-time image backfills (Sample Image + Visual Aids/Video, both copy from the
   frozen Airtable BIM Element Templates table) — idempotent, safe to re-run ─────── */

function BimImageBackfillBar({ addToast }) {
  const [busy, setBusy] = useState(null) // 'sample' | 'visual' | null

  async function run(kind) {
    setBusy(kind)
    try {
      const d = kind === 'sample' ? await backfillBimTemplateImages() : await backfillBimVisualAids()
      addToast(kind === 'sample'
        ? `Sample images: ${d.copied} copied, ${d.failed} failed (of ${d.scanned})`
        : `Visual aids/video: ${d.copied} image sets, ${d.videoLinked} video links, ${d.failed} failed (of ${d.scanned})`)
    } catch (e) { addToast('Backfill failed: ' + (e.message || '')) }
    finally { setBusy(null) }
  }

  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 10 }}>
      <span style={{ fontSize: 10, fontFamily: 'DM Mono, monospace', color: 'var(--aj-text-muted)', textTransform: 'uppercase' }}>One-time Airtable image carry-over</span>
      <button onClick={() => run('sample')} disabled={!!busy} style={{ background: 'none', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, padding: '3px 9px', fontSize: 11, fontFamily: 'DM Sans, sans-serif', color: 'var(--aj-text-mid)', cursor: busy ? 'default' : 'pointer' }}>
        {busy === 'sample' ? 'Running…' : 'Backfill sample images'}
      </button>
      <button onClick={() => run('visual')} disabled={!!busy} style={{ background: 'none', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, padding: '3px 9px', fontSize: 11, fontFamily: 'DM Sans, sans-serif', color: 'var(--aj-text-mid)', cursor: busy ? 'default' : 'pointer' }}>
        {busy === 'visual' ? 'Running…' : 'Backfill visual aids/video'}
      </button>
    </div>
  )
}

/* ── List (grouped: system template → element templates) ─────────────────────── */

export function BimTemplatesList({ onOpenTemplate, addToast }) {
  const [systems, setSystems] = useState(null)   // null = loading
  const [search, setSearch] = useState('')
  const [newFor, setNewFor] = useState(null)     // system template id a new element is being added to
  const [newName, setNewName] = useState('')
  const [savingNew, setSavingNew] = useState(false)

  useEffect(() => {
    getBimTemplates().then(setSystems).catch(() => setSystems([]))
  }, [])

  async function handleCreate(sysId) {
    const name = newName.trim()
    if (!name || savingNew) return
    setSavingNew(true)
    try {
      const tpl = await saveBimElementTemplate(null, { system_template_id: sysId, name, ready_for_use: false })
      setSystems(prev => (prev || []).map(s => s.id === sysId ? { ...s, elements: [...s.elements, { ...tpl, criteria: [] }] } : s))
      setNewFor(null); setNewName('')
      addToast('Element template created')
      onOpenTemplate({ id: tpl.id, name: tpl.name, type: 'bim' })
    } catch (e) {
      addToast('Create failed: ' + (e.message || 'unknown error'))
    } finally { setSavingNew(false) }
  }

  const q = search.toLowerCase()

  return (
    <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <div style={{ padding: '0 24px 12px', flexShrink: 0 }}>
        <BimImageBackfillBar addToast={addToast} />
        <input
          style={{ ...S.input, width: 260, display: 'inline-block' }}
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search BIM element templates…"
        />
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: '0 24px 24px' }}>
        {systems === null && (
          <div style={{ padding: '40px 0', textAlign: 'center', color: 'var(--aj-text-mid)', fontSize: 13, fontFamily: 'DM Sans, sans-serif' }}>Loading templates…</div>
        )}
        {Array.isArray(systems) && systems.map(sys => {
          const els = sys.elements.filter(el => !q || el.name.toLowerCase().includes(q))
          if (q && !els.length) return null
          return (
            <div key={sys.id} style={{ marginBottom: 6 }}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '10px 10px 6px',
                borderBottom: '0.5px solid var(--aj-border-mid)',
              }}>
                <span style={{ fontSize: 11, fontFamily: 'Montserrat, sans-serif', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--aj-text)' }}>{sys.name}</span>
                <span style={{ fontSize: 10.5, fontFamily: 'DM Mono, monospace', color: 'var(--aj-text-muted)' }}>{sys.elements.length} element{sys.elements.length !== 1 ? 's' : ''}</span>
                <div style={{ flex: 1 }} />
                <button
                  onClick={() => { setNewFor(newFor === sys.id ? null : sys.id); setNewName('') }}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11.5, fontFamily: 'DM Sans, sans-serif', color: '#1a6fa8', padding: 0 }}
                >+ New element</button>
              </div>
              {newFor === sys.id && (
                <div style={{ display: 'flex', gap: 8, padding: '8px 10px', alignItems: 'center' }}>
                  <input
                    autoFocus
                    style={{ ...S.input, width: 280, display: 'inline-block' }}
                    value={newName}
                    onChange={e => setNewName(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') handleCreate(sys.id); if (e.key === 'Escape') setNewFor(null) }}
                    placeholder="Element name…"
                  />
                  <button onClick={() => handleCreate(sys.id)} disabled={savingNew} style={S.btnPrimary}>{savingNew ? 'Creating…' : 'Create'}</button>
                </div>
              )}
              {els.map(el => (
                <div
                  key={el.id}
                  onClick={() => onOpenTemplate({ id: el.id, name: el.name, type: 'bim' })}
                  style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 10px 10px 22px', borderBottom: '0.5px solid var(--aj-border)', cursor: 'pointer' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--aj-bg-subtle)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontFamily: 'DM Sans, sans-serif', color: 'var(--aj-text)', fontWeight: 500 }}>{el.name}</div>
                    <div style={{ fontSize: 10.5, fontFamily: 'DM Mono, monospace', color: 'var(--aj-text-mid)', marginTop: 2 }}>
                      {BIM_PHASES.map(p => {
                        const n = el.criteria.filter(c => c.phase === p).length
                        return n ? BIM_PHASE_LABELS[p] + ' ' + n : null
                      }).filter(Boolean).join(' · ') || 'No criteria yet'}
                    </div>
                  </div>
                  {!el.ready_for_use && (
                    <span style={{ fontSize: 9, fontFamily: 'DM Mono, monospace', textTransform: 'uppercase', color: '#b8540a', border: '0.5px solid rgba(184,84,10,0.35)', borderRadius: 20, padding: '1px 7px' }}>Draft</span>
                  )}
                  <div style={{ fontSize: 11, color: 'var(--aj-text-muted)', fontFamily: 'DM Mono, monospace' }}>→</div>
                </div>
              ))}
            </div>
          )
        })}
      </div>
    </div>
  )
}

/* ── Criterion row (editor) ───────────────────────────────────────────────────── */

function CriterionEditorRow({ criterion, onSave, onDelete, dragHandleProps, isDragging }) {
  const [text, setText] = useState(criterion.text)
  const [saving, setSaving] = useState(false)
  const dirty = text.trim() !== criterion.text

  async function commitText() {
    const t = text.trim()
    if (!t || t === criterion.text) { setText(criterion.text); return }
    setSaving(true)
    await onSave({ text: t })
    setSaving(false)
  }

  async function toggleKind() {
    setSaving(true)
    await onSave({ kind: criterion.kind === 'coord' ? 'model' : 'coord' })
    setSaving(false)
  }

  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 8, padding: '6px 4px',
      borderBottom: '0.5px solid var(--aj-border)', opacity: isDragging ? 0.5 : 1,
      background: isDragging ? 'var(--aj-bg-subtle)' : 'transparent',
    }}>
      <span {...dragHandleProps} title="Drag to reorder" style={{ cursor: 'grab', color: 'var(--aj-text-muted)', fontSize: 12, padding: '4px 2px', userSelect: 'none', touchAction: 'none' }}>⠿</span>
      <button
        onClick={toggleKind}
        title={criterion.kind === 'coord' ? 'COORD — coordinate with consultants. Click to make a modeling criterion.' : 'Modeling criterion. Click to mark as COORD (coordinate with consultants).'}
        style={{
          fontFamily: 'DM Mono, monospace', fontSize: 8.5, textTransform: 'uppercase', flexShrink: 0, marginTop: 5,
          color: KIND_COLORS[criterion.kind], border: '0.5px solid currentColor', opacity: criterion.kind === 'coord' ? 1 : 0.55,
          borderRadius: 20, padding: '1px 6px', background: 'transparent', cursor: 'pointer',
        }}
      >{criterion.kind === 'coord' ? 'COORD' : 'MODEL'}</button>
      <textarea
        value={text}
        rows={Math.max(1, Math.ceil(text.length / 90))}
        onChange={e => setText(e.target.value)}
        onBlur={commitText}
        onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); e.target.blur() } }}
        style={{
          flex: 1, border: 'none', outline: 'none', resize: 'none', background: 'transparent',
          fontSize: 12.5, fontFamily: 'DM Sans, sans-serif', color: 'var(--aj-text)', lineHeight: 1.45,
          borderBottom: dirty ? '1px dashed var(--aj-border-mid)' : '1px solid transparent',
        }}
      />
      {saving && <span style={{ fontSize: 10, color: 'var(--aj-text-muted)', fontFamily: 'DM Mono, monospace', marginTop: 5 }}>…</span>}
      <button onClick={onDelete} title="Delete criterion" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--aj-text-muted)', fontSize: 13, padding: '2px 4px' }}>×</button>
    </div>
  )
}

/* ── Visual Aids + Video Tutorial (per phase) ─────────────────────────────────── */

function PhaseMedia({ templateId, phase, visualAids, videoUrl, onTemplateChange, onVideoUrlCommit, addToast }) {
  const [videoDraft, setVideoDraft] = useState(videoUrl || '')
  const [uploading, setUploading] = useState(false)
  const fileRef = useRef(null)
  useEffect(() => { setVideoDraft(videoUrl || '') }, [videoUrl])

  async function handleFile(e) {
    const file = e.target.files && e.target.files[0]
    e.target.value = ''
    if (!file) return
    setUploading(true)
    try {
      const tpl = await uploadBimVisualAid(templateId, phase, file)
      onTemplateChange(tpl)
    } catch (err) { addToast('Upload failed: ' + (err.message || '')) }
    finally { setUploading(false) }
  }

  async function handleRemove(url) {
    try {
      const tpl = await removeBimVisualAid(templateId, phase, url)
      onTemplateChange(tpl)
    } catch (err) { addToast('Remove failed: ' + (err.message || '')) }
  }

  function commitVideo() {
    const v = videoDraft.trim()
    if (v === (videoUrl || '')) return
    onVideoUrlCommit(v)
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0 10px', flexWrap: 'wrap' }}>
      <span style={{ fontSize: 10, fontFamily: 'DM Mono, monospace', color: 'var(--aj-text-muted)', textTransform: 'uppercase' }}>Visual aids</span>
      {visualAids.map(url => (
        <div key={url} style={{ position: 'relative', width: 34, height: 34 }}>
          <a href={url} target="_blank" rel="noreferrer">
            <img src={url} alt="" style={{ width: 34, height: 34, objectFit: 'cover', borderRadius: 4, border: '0.5px solid var(--aj-border-mid)', display: 'block' }} />
          </a>
          <button
            onClick={() => handleRemove(url)}
            title="Remove"
            style={{
              position: 'absolute', top: -5, right: -5, width: 15, height: 15, borderRadius: '50%',
              background: '#07272D', color: '#fff', border: 'none', fontSize: 10, lineHeight: 1, cursor: 'pointer', padding: 0,
            }}
          >×</button>
        </div>
      ))}
      <button
        onClick={() => fileRef.current && fileRef.current.click()}
        disabled={uploading}
        style={{
          width: 34, height: 34, borderRadius: 4, border: '1px dashed var(--aj-border-mid)', background: 'none',
          color: 'var(--aj-text-muted)', cursor: uploading ? 'default' : 'pointer', fontSize: 15,
        }}
        title="Add visual aid image"
      >{uploading ? '…' : '+'}</button>
      <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFile} />

      <span style={{ fontSize: 10, fontFamily: 'DM Mono, monospace', color: 'var(--aj-text-muted)', textTransform: 'uppercase', marginLeft: 8 }}>Video tutorial</span>
      <input
        value={videoDraft}
        onChange={e => setVideoDraft(e.target.value)}
        onBlur={commitVideo}
        onKeyDown={e => { if (e.key === 'Enter') e.target.blur() }}
        placeholder="https://…"
        style={{ ...S.input, width: 220, display: 'inline-block' }}
      />
    </div>
  )
}

/* ── Editor (one element template) ────────────────────────────────────────────── */

export function BimTemplateEditorView({ template, onBack, addToast }) {
  const [data, setData] = useState(null)         // the full element template (+ criteria)
  const [adding, setAdding] = useState(null)     // phase an add-row is open for
  const [addText, setAddText] = useState('')
  const savingRef = useRef(false)

  useEffect(() => {
    // The list payload has everything; re-fetch the tree and pick this template so a
    // deep-open (or a stale list) still lands on fresh data.
    getBimTemplates().then(systems => {
      for (const s of (systems || [])) {
        const el = s.elements.find(e => e.id === template.id)
        if (el) { setData({ ...el, systemName: s.name }); return }
      }
      setData(false)   // not found
    }).catch(() => setData(false))
  }, [template.id])

  function patchLocal(patch) { setData(d => ({ ...d, ...patch })) }

  async function saveField(field, value) {
    patchLocal({ [field]: value })
    try { await saveBimElementTemplate(template.id, { [field]: value }) }
    catch (e) { addToast('Save failed: ' + (e.message || '')) }
  }

  async function handleAdd(phase) {
    const text = addText.trim()
    if (!text || savingRef.current) return
    savingRef.current = true
    try {
      const c = await saveBimCriterion({ elementTemplateId: template.id, phase, text, kind: 'model' })
      patchLocal({ criteria: [...data.criteria, c] })
      setAddText('')
      // keep the add-row open — entering several criteria in a run is the common case
    } catch (e) { addToast('Add failed: ' + (e.message || '')) }
    finally { savingRef.current = false }
  }

  async function handleSaveCriterion(c, patch) {
    patchLocal({ criteria: data.criteria.map(x => x.id === c.id ? { ...x, ...patch } : x) })
    try { await saveBimCriterion({ id: c.id, ...patch }) }
    catch (e) { addToast('Save failed: ' + (e.message || '')) }
  }

  async function handleDeleteCriterion(c) {
    const ok = await confirmDialog({
      title: 'Delete criterion?',
      message: 'Live elements using this template lose this checklist line (their other checked state is untouched).',
      confirmLabel: 'Delete', danger: true,
    })
    if (!ok) return
    patchLocal({ criteria: data.criteria.filter(x => x.id !== c.id) })
    try { await deleteBimCriterion(c.id) }
    catch (e) { addToast('Delete failed: ' + (e.message || '')) }
  }

  function handleTemplateMediaChange(tpl) { patchLocal({ visual_aids: tpl.visual_aids }) }

  async function handleVideoUrlCommit(phase, url) {
    const next = { ...(data.video_tutorial_urls || {}) }
    if (url) next[phase] = url; else delete next[phase]
    await saveField('video_tutorial_urls', next)
  }

  async function handleReorder(phase, newPhaseList) {
    const others = data.criteria.filter(c => c.phase !== phase)
    patchLocal({ criteria: [...others, ...newPhaseList] })
    try { await reorderBimCriteria(newPhaseList.map(c => c.id)) }
    catch (e) { addToast('Reorder failed: ' + (e.message || '')) }
  }

  async function handleDeleteTemplate() {
    const ok = await confirmDialog({
      title: 'Delete element template?',
      message: 'Deletes "' + (data?.name || template.name) + '" and ALL its criteria. Live elements created from it keep their history but lose their checklist.',
      confirmLabel: 'Delete template', danger: true,
    })
    if (!ok) return
    try {
      await deleteBimElementTemplate(template.id)
      addToast('Template deleted')
      onBack()
    } catch (e) { addToast('Delete failed: ' + (e.message || '')) }
  }

  if (data === null) {
    return <div style={{ padding: 40, textAlign: 'center', color: 'var(--aj-text-mid)', fontSize: 13, fontFamily: 'DM Sans, sans-serif' }}>Loading…</div>
  }
  if (data === false) {
    return <div style={{ padding: 40, textAlign: 'center', color: 'var(--aj-text-mid)', fontSize: 13, fontFamily: 'DM Sans, sans-serif' }}>Template not found.</div>
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      {/* Editor header */}
      <div style={{ padding: '14px 24px 10px', flexShrink: 0, borderBottom: '0.5px solid var(--aj-border)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={onBack} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--aj-text-mid)', fontSize: 13, fontFamily: 'DM Sans, sans-serif', padding: 0 }}>← Back</button>
          <div style={{ fontSize: 10.5, fontFamily: 'DM Mono, monospace', color: 'var(--aj-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{data.systemName}</div>
          <div style={{ flex: 1 }} />
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontFamily: 'DM Sans, sans-serif', color: 'var(--aj-text-mid)', cursor: 'pointer' }}>
            <input type="checkbox" checked={!!data.ready_for_use} onChange={e => saveField('ready_for_use', e.target.checked)} />
            Ready for use
          </label>
          <button onClick={handleDeleteTemplate} style={{ background: 'none', border: '0.5px solid rgba(192,57,43,0.4)', color: '#c0392b', borderRadius: 4, padding: '4px 10px', fontSize: 11.5, fontFamily: 'DM Sans, sans-serif', cursor: 'pointer' }}>Delete</button>
        </div>
        <input
          value={data.name}
          onChange={e => patchLocal({ name: e.target.value })}
          onBlur={e => { const v = e.target.value.trim(); if (v && v !== template.name) saveField('name', v) }}
          style={{ ...S.input, fontSize: 16, fontWeight: 600, marginTop: 8, border: 'none', background: 'transparent', padding: '2px 0' }}
        />
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '12px 24px 32px' }}>
        {/* Per-phase criteria sections */}
        {BIM_PHASES.map(phase => {
          const list = data.criteria.filter(c => c.phase === phase).sort((a, b) => a.sort_order - b.sort_order)
          return (
            <div key={phase} style={{ marginBottom: 18 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: '0.5px solid var(--aj-border-mid)' }}>
                <span style={{ fontSize: 11, fontFamily: 'Montserrat, sans-serif', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--aj-text)' }}>
                  {BIM_PHASE_LABELS[phase]} Criteria
                </span>
                <span style={{ fontSize: 10.5, fontFamily: 'DM Mono, monospace', color: 'var(--aj-text-muted)' }}>{list.length}</span>
                <div style={{ flex: 1 }} />
                <button
                  onClick={() => { setAdding(adding === phase ? null : phase); setAddText('') }}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11.5, fontFamily: 'DM Sans, sans-serif', color: '#1a6fa8', padding: 0 }}
                >+ Add criterion</button>
              </div>
              <PhaseMedia
                templateId={data.id}
                phase={phase}
                visualAids={(data.visual_aids || {})[phase] || []}
                videoUrl={(data.video_tutorial_urls || {})[phase] || ''}
                onTemplateChange={handleTemplateMediaChange}
                onVideoUrlCommit={url => handleVideoUrlCommit(phase, url)}
                addToast={addToast}
              />
              {list.length > 0 && (
                <DragList
                  items={list}
                  onReorder={newList => handleReorder(phase, newList)}
                  renderItem={(c, dragHandleProps, isDragging) => (
                    <CriterionEditorRow
                      criterion={c}
                      onSave={patch => handleSaveCriterion(c, patch)}
                      onDelete={() => handleDeleteCriterion(c)}
                      dragHandleProps={dragHandleProps}
                      isDragging={isDragging}
                    />
                  )}
                />
              )}
              {adding === phase && (
                <div style={{ display: 'flex', gap: 8, padding: '8px 0 0 24px', alignItems: 'flex-start' }}>
                  <textarea
                    autoFocus
                    value={addText}
                    rows={2}
                    onChange={e => setAddText(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAdd(phase) }
                      if (e.key === 'Escape') { setAdding(null); setAddText('') }
                    }}
                    placeholder="New criterion — Enter to add, Shift+Enter for a newline, Esc to close"
                    style={{ ...S.input, flex: 1, resize: 'vertical' }}
                  />
                  <button onClick={() => handleAdd(phase)} style={S.btnPrimary}>Add</button>
                </div>
              )}
              {list.length === 0 && adding !== phase && (
                <div style={{ padding: '8px 0 0 24px', fontSize: 11.5, fontFamily: 'DM Sans, sans-serif', color: 'var(--aj-text-muted)' }}>
                  {phase === 'cd' ? 'No CD criteria — CD is drawing production; elements ride the DD-tuned model.' : 'No criteria yet.'}
                </div>
              )}
            </div>
          )
        })}

        {/* Template-level fields */}
        <div style={{ marginTop: 8, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div>
            <label style={S.label}>DD Coordination (consultants)</label>
            <textarea
              value={data.dd_coordination || ''}
              rows={3}
              onChange={e => patchLocal({ dd_coordination: e.target.value })}
              onBlur={e => saveField('dd_coordination', e.target.value || null)}
              style={{ ...S.input, resize: 'vertical' }}
              placeholder={'Landscape:\nCivil:\nSurvey:'}
            />
          </div>
          <div>
            <label style={S.label}>Notes</label>
            <textarea
              value={data.notes || ''}
              rows={3}
              onChange={e => patchLocal({ notes: e.target.value })}
              onBlur={e => saveField('notes', e.target.value || null)}
              style={{ ...S.input, resize: 'vertical' }}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
